import React, { useCallback, useEffect, useState } from 'react';
import { ChevronRight, Search, Clock } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { takeAdminParam } from '../../lib/adminLinks';
import { HELP_SECTIONS, HELP_BLOCKS, HELP_INDEX, searchHelp, formatReviewed } from './helpContent';

const sectionOf = (id) => HELP_INDEX.find((t) => t.id === id)?.sectionId || null;

const HelpTopic = ({ item, active }) => (
  <div id={`help-topic-${item.id}`}
    className={`border-l-2 pl-3 py-1.5 scroll-mt-36 ${active ? 'border-amber-500 bg-amber-50/70 rounded-r' : 'border-neutral-200'}`}
    data-testid={`help-topic-${item.id}`}>
    <div className="flex flex-wrap items-baseline justify-between gap-x-3">
      <p className="text-sm font-medium text-neutral-800">{item.title}</p>
      <span className="text-[11px] text-neutral-400" data-testid={`help-topic-${item.id}-reviewed`}>Last reviewed {formatReviewed(item.reviewed)}</span>
    </div>
    <p className="text-xs text-neutral-600 leading-relaxed mt-0.5">{item.desc}</p>
    {item.points && (
      <ul className="mt-1.5 space-y-1 list-disc pl-4 text-xs text-neutral-600 leading-relaxed">
        {item.points.map((pt) => <li key={pt}>{pt}</li>)}
      </ul>
    )}
    {HELP_BLOCKS.filter(([k]) => item[k]?.length).map(([k, label]) => (
      <div key={k} className="mt-2" data-testid={`help-topic-${item.id}-${k}`}>
        <p className={`text-[11px] font-semibold uppercase tracking-wider ${k === 'careful' ? 'text-red-700' : 'text-neutral-500'}`}>{label}</p>
        <ul className="mt-0.5 space-y-1 list-disc pl-4 text-xs text-neutral-600 leading-relaxed">
          {item[k].map((pt) => <li key={pt}>{pt}</li>)}
        </ul>
      </div>
    ))}
  </div>
);

// Documentation review list: every topic, oldest "Last reviewed" date first.
const ReviewList = ({ onOpen }) => {
  const rows = [...HELP_INDEX].sort((a, b) => (a.reviewed || '').localeCompare(b.reviewed || '') || a.sectionTitle.localeCompare(b.sectionTitle));
  return (
    <Card data-testid="help-review-list">
      <p className="px-4 pt-3 pb-2 text-xs text-neutral-500 border-b">All topics, oldest "Last reviewed" date first (New York dates). Review the topics at the top first.</p>
      <CardContent className="p-0 divide-y">
        {rows.map((t) => (
          <button key={t.id} type="button" onClick={() => onOpen(t.id)} className="w-full flex items-center justify-between gap-3 px-4 py-2 text-left hover:bg-neutral-50 transition-colors" data-testid={`help-review-row-${t.id}`}>
            <span className="text-sm text-neutral-800">{t.title} <span className="text-xs text-neutral-400">· {t.page || t.sectionTitle}</span></span>
            <span className="text-xs text-neutral-500 shrink-0">{formatReviewed(t.reviewed)}</span>
          </button>
        ))}
      </CardContent>
    </Card>
  );
};

export default function HelpGuide() {
  const [search, setSearch] = useState('');
  const [view, setView] = useState('guide');
  const [topic, setTopic] = useState(() => takeAdminParam('topic'));
  const [expanded, setExpanded] = useState(() => sectionOf(topic));
  const open = useCallback((id) => { setView('guide'); setSearch(''); setTopic(id); setExpanded(sectionOf(id)); }, []);
  useEffect(() => {
    const onPop = () => { const t = takeAdminParam('topic'); if (t) open(t); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open]);
  useEffect(() => {
    if (topic) setTimeout(() => document.getElementById(`help-topic-${topic}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 80);
  }, [topic, expanded]);

  const hits = search.trim() ? new Set(searchHelp(search).map((t) => t.id)) : null;
  const filtered = hits ? HELP_SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => hits.has(i.id)) })).filter((s) => s.items.length) : HELP_SECTIONS;

  return (
    <div className="space-y-6" data-testid="help-guide">
      <div>
        <h2 className="text-xl font-semibold text-neutral-900">Help & Feature Guide</h2>
        <p className="text-sm text-neutral-500 mt-1">Organised like the admin menu. Use the "Help" link at the top of any admin page, or press <kbd className="px-1 py-0.5 rounded border bg-neutral-50 text-[11px]">?</kbd> anywhere in the admin to search.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input placeholder="Search pages, topics and help text..." value={search} onChange={(e) => { setSearch(e.target.value); setView('guide'); }} className="pl-10" data-testid="help-search" />
        </div>
        <button type="button" onClick={() => setView(view === 'review' ? 'guide' : 'review')} data-testid="help-review-toggle"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${view === 'review' ? 'bg-neutral-900 text-white border-neutral-900' : 'border-neutral-200 text-neutral-600 hover:bg-neutral-50'}`}>
          <Clock className="w-3.5 h-3.5" />Oldest reviewed first
        </button>
      </div>
      {view === 'review' ? <ReviewList onOpen={open} /> : (
        <div className="space-y-3">
          {filtered.map((section) => {
            const Icon = section.icon;
            const isOpen = expanded === section.id || !!hits;
            return (
              <Card key={section.id} className="overflow-hidden" data-testid={`help-section-${section.id}`}>
                <button onClick={() => setExpanded(expanded === section.id ? null : section.id)} className="w-full flex items-center gap-3 p-4 text-left hover:bg-neutral-50 transition-colors" data-testid={`help-section-toggle-${section.id}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${section.color}`}><Icon className="w-4 h-4" /></div>
                  <div className="flex-1">
                    <p className="font-medium text-neutral-900 text-sm">{section.title}</p>
                    <p className="text-xs text-neutral-500">{section.items.map((i) => i.title).slice(0, 5).join(' · ')}{section.items.length > 5 ? ' …' : ''}</p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-neutral-300 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>
                {isOpen && (
                  <CardContent className="pt-0 pb-4 px-4">
                    <div className="space-y-3 ml-12">
                      {section.items.map((item) => <HelpTopic key={item.id} item={item} active={item.id === topic} />)}
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
          {filtered.length === 0 && (
            <div className="py-12 text-center text-neutral-400">
              <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm">No matching help topics found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
